import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore, selectUser, canAccessMenuSite, type MenuKey } from "@safers/auth";
import { Spinner } from "@safers/ui/atoms";
import { dashboardService } from "@/services";
import { controlKeys } from "@/pages/control/useControlData";
import { useIsMobile } from "@/hooks/useIsMobile";
import { readLastSite } from "@/lib/lastVisit";
import { NoSiteAccess } from "@/components/NoSiteAccess";
import { areaPath, type Area } from "@/routes/paths";
import { worstSeverity } from "@/lib/map";

const MENU_BY_AREA: Record<Area, MenuKey> = {
  control: "CONTROL",
  health: "HEALTH",
  cctv: "CCTV",
};

/**
 * 전국 주소를 받아 **볼 수 있는 현장 하나로** 넘긴다.
 *
 *   기억해 둔 마지막 현장  →  가장 위험한 현장  →  목록의 첫 현장
 *
 * 고르는 규칙을 화면마다 두면 헤더·탭·팔레트가 제각기 다른 현장으로 보낸다.
 */
function SiteResolver({ area }: { area: Area }) {
  const user = useAuthStore(selectUser);
  const menu = MENU_BY_AREA[area];
  const remembered = readLastSite(user?.username ?? null);
  const canUseRemembered = remembered != null && canAccessMenuSite(user, remembered, menu);

  const { data, isPending } = useQuery({
    queryKey: controlKeys.national(),
    queryFn: () => dashboardService.getAllSites(),
    staleTime: 60_000,
    enabled: !canUseRemembered,
  });

  if (canUseRemembered) return <Navigate to={areaPath(area, remembered)} replace />;

  if (isPending) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const sites = (data?.siteStatus.regions.flatMap((r) => r.sites) ?? []).filter((s) =>
    canAccessMenuSite(user, s.siteId, menu)
  );
  const worst = worstSeverity(sites.map((s) => s.severity));
  const target = sites.find((s) => s.severity === worst) ?? sites[0];

  // 보낼 현장이 없으면 안내로 떨어진다. 전국 주소로 되돌리면 여기로 다시 와서 돈다.
  if (!target) return <NoSiteAccess />;

  return <Navigate to={areaPath(area, target.siteId)} replace />;
}

/**
 * 영역의 전국 주소를 받는 자리.
 *
 * **폰에는 전국이 없다.** 지도가 빠지고 화면이 좁아 전국을 훑는 일이 성립하지 않는다.
 * 그래서 폰에서는 현장 하나로 넘기고, 넓은 화면에서만 전국 지면을 그린다.
 *
 * CCTV 는 `children` 이 없다 — 전국 지면 자체가 아직 없어서 폭과 무관하게 현장으로
 * 넘긴다. 전체 현장 탐색기(#337)가 생기면 그 화면을 `children` 으로 넘기면 된다.
 */
export function AreaEntry({ area, children }: { area: Area; children?: ReactNode }) {
  const isMobile = useIsMobile();
  if (children && !isMobile) return <>{children}</>;
  return <SiteResolver area={area} />;
}
